import { approveDeliverynoteDraft } from 'client/actions/DocumentsV1/deliverynoteActions'
import { clearDraftV1 } from 'client/actions/DocumentsV1/draftV1/draftV1Actions'
import { updateApprovedThunkV1 } from 'client/actions/DocumentsV1/draftV1/draftV1CreateUpdateThunks'
import { approveReceiptDraft } from 'client/actions/DocumentsV1/receiptActions'
import { getDefaultIBAN, getTinkLink } from 'client/actions/helpers/tink'
import { getSubscription } from 'client/actions/subscriptionManagement'
import { getDocument, getDocumentV1 } from 'client/api/document'
import { hasUsedFreeTinkUnpaidPlan } from 'client/components/Modal/CompanyInformationModal/helper'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import useGetCustomer from 'client/hooks/useGetCustomer'
import { EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'
import { ModalInterface } from 'client/Interfaces/ModalInterface'
import { l } from 'client/Other/Translation'
import { getDraftV1Selector } from 'client/reducers/draftV1Reducer'
import { hasModuleAvailable, moduleNames, tradeNameV1Selector } from 'client/reducers/settings'

import * as EmailApi from '../api/email'
import { attachmentDeleteEndpoint, attachmentUploadEndpoint } from '../api/email'
import { PremiumFeatureType } from '../components/Modal/PremiumFeatureIntermediaryModal'
import { Analytics, checkPlacement } from '../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  Events,
  PremiumModalOrigin,
  Result,
  trackingFields,
} from '../Global/AnalyticsEvents'
import { refreshEstimate } from '../graphQueries/estimates'
import { refreshDocument } from '../graphQueries/invoices'
import { DocumentType, DocumentTypes, DraftInterface } from '../Interfaces/Draft/DraftInterface'
import { InvoiceDeliveryEmailInterface, InvoiceEmailInterface } from '../Interfaces/EmailInterface'
import { EstimateInterface, InvoiceInterface } from '../Interfaces/Invoice/InvoiceInterface'
import { NavigationInterface } from '../Interfaces/NavigationInterface'
import { OrderInterface, OrderType } from '../Interfaces/OrderInterface'
import { StateInterface } from '../Interfaces/StateInterface'
import { QuotaTypes } from '../Interfaces/Subscription'
import { constructDocumentFileName, constructDocumentFileNameV1 } from '../Other/functions/documentFunctions'
import { handleFieldsBeforeSave } from '../Other/functions/modelFunctions'
import { Immutable } from '../Other/Immutable'
import { isPayNowActive, isPayNowConnected } from '../reducers/payNowManagement'
import {
  doesCurrentPlanSupportFeature,
  hasDueOrDisputedConsumables,
} from '../reducers/Subscription/companySubscription'
import exportedActions, { approveInvoiceDraft } from './draft'
import { updateEstimate } from './estimate'
import { approveDraft, saveDraft } from './estimateDraft'
import { getTradeName } from './helpers/draft'
import { actionCreatorAsync, IThunkAction } from './index'
import { updateInvoice } from './invoice'
import { navigateAddModal, navigateRemoveModal, setNavigation, setNavigationPath } from './navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from './ongoing'

export const isXRechnungSupportedCountry = (country) => {
  return ['DE', 'CH', 'AT'].includes(country)
}

type SendOption = 'all' | 'first'

export const sendEstimateEmail = (email: InvoiceEmailInterface, estimateId: number, captchaToken, locale?: string) => {
  return async (dispatch, getState) => {
    await dispatch(
      actionCreatorAsync('SEND_ESTIMATE_EMAIL', async () => {
        if (email.saveAsDefaultMessage) {
          const formattedMessageBody = email?.message?.trim().replace(/\n/g, '<br />')
          EmailApi.postEmailTemplate(DocumentTypes.ESTIMATE, locale || getState().token.locale, {
            body: formattedMessageBody,
          })
        }
        dispatch(sendEmail(_createEstimateEmail(email, getState(), captchaToken), DocumentTypes.ESTIMATE, estimateId))
        Analytics.trackEvent(Events.sendDocument, {
          type: AnalyticsType.action,
          category: Category.document,
          result: Result.success,
          placement: checkPlacement(window.location.pathname),
          documentType: AnalyticsObject.estimate,
          documentID: estimateId,
          deliveryType: trackingFields.deliveryType.email,
        })
        Segment.track(SegmentEvents.estimates.sent, { estimate_id: estimateId, sending_method: 'email' })
      }),
    )
  }
}

export const sendEmail = (email: InvoiceDeliveryEmailInterface, documentType: DocumentType, documentId: number) => {
  return async (dispatch, getState) => {
    try {
      email.body = email.body.trim().replace(/\n/g, '<br />')
      const sendReq = await EmailApi.sendEmail(email, documentType, documentId)
      // refresh document in apollo graphql cache, so that list has correct status for the doc
      if (documentType === DocumentTypes.ESTIMATE) {
        void refreshEstimate(documentId)
      } else {
        void refreshDocument(documentId, documentType)
      }
      return sendReq
    } catch (error) {
      const state = getState()
      if (error.response?.status === 422) {
        Segment.track(SegmentEvents.debug.blockedInvoiceSending, {
          context: checkPlacement(window.location?.pathname),
          window_absolute_url: window.location?.href,
          country: state?.company?.country,
          error_message: error?.message,
        })
        dispatch(setNavigationPath('/blocked'))
      }
    }
  }
}

export const sendInvoiceEmail = (
  email: InvoiceEmailInterface,
  invoiceId: number,
  docType: DocumentType = DocumentTypes.INVOICE,
  captchaToken: string,
  orderData?: {
    projectName: string | null
    paymentSchedule?: OrderType
    orderItemsLength?: number
    scheduleId: OrderInterface['scheduleId']
  },
  locale?: string,
) => {
  // TODO: Should send event like in sendEstimateEmail?
  return async (dispatch, getState) => {
    let onlinePayments: boolean | 'notEnabled' = email.acceptStripePayment
    if (!onlinePayments) {
      onlinePayments = isPayNowConnected(getState()) ? false : 'notEnabled'
    }
    const isInvoice = docType === DocumentTypes.INVOICE
    const tinkPaymentUrl =
      isInvoice && email.tinkPaymentEnabled && email.tinkSupportedAcccount
        ? await getTinkLink(getState(), email.tinkPaymentEnabled, invoiceId)
        : ''
    const payload = {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.success,
      documentType: AnalyticsObject.invoice,
      placement: checkPlacement(window.location.pathname),
      documentID: invoiceId,
      deliveryType: trackingFields.deliveryType.email,
      onlinePayments,
      orderData,
      invoiceAttachmentType: `${trackingFields.deliveryType.pdf}${
        email.attachXRechnung ? ',' + trackingFields.deliveryType.xRechnung : ''
      }`,
    }
    Analytics.trackEvent(Events.sendDocument, payload)
    if (isInvoice) {
      Segment.track(SegmentEvents.invoice.sent, {
        invoice_id: invoiceId,
        sending_method: 'email',
        default_attachment_type: email.attachXRechnung ? 'xrechnung' : 'pdf',
      })
    }
    if (email.saveAsDefaultMessage) {
      const formattedMessageBody = email?.message?.trim().replace(/\n/g, '<br />')
      void (await EmailApi.postEmailTemplate(docType, locale || getState().token.locale, {
        body: formattedMessageBody,
      }))
    }
    const emailPayload = {
      to: email.to,
      subject: email.subject,
      body: email.message.trim().replace(/\n/g, '<br />'),
      acceptStripePayment: email.acceptStripePayment,
      ...(tinkPaymentUrl && isInvoice ? { tinkPaymentUrl } : {}),
      attachmentUuids: email.attachments,
      ...(email.attachXRechnung ? { attachXRechnung: email.attachXRechnung } : {}),
      captchaToken,
    }
    if (email?.bcc && email.sendCopyToMe) {
      emailPayload.bcc = [email?.bcc, email.sender].join(',')
    } else if (email?.bcc) {
      emailPayload.bcc = email?.bcc
    } else if (email.sendCopyToMe) {
      emailPayload.bcc = email?.sender
    }
    if (email?.cc) {
      emailPayload.cc = email?.cc
    }
    dispatch(sendEmail(emailPayload, docType, invoiceId))
  }
}

// First personal email, then company email, finally login email
export const getEmailAddress = (state: StateInterface): string => {
  const {
    company,
    settings: { values },
    user,
  } = state
  const loginEMail = user.email
  const companyEmail = company.email
  const personEmail = values.company.tradeNames[0].contact.email
  return personEmail || companyEmail || loginEMail
}

function hasAttachmentAccess(subscription) {
  return doesCurrentPlanSupportFeature(subscription, QuotaTypes.EMAIL_ATTACHMENT)
}

function getOrderData(invoiceDocument) {
  return invoiceDocument.order
    ? {
        projectName: invoiceDocument.order.name,
        orderType: invoiceDocument.order.orderType,
        orderItemsLength: invoiceDocument.order.items.length,
        scheduleId: invoiceDocument.order.scheduleId,
      }
    : null
}

const sendInvoiceEmailWhenReady = async (invoice, email, type, captchaToken, dispatch) => {
  email.entityId = invoice.invoice_id
  const document = invoice.invoicedocument || invoice.creditnotedocument
  await dispatch(
    sendInvoiceEmail(
      email,
      invoice.id,
      document.document,
      captchaToken,
      getOrderData(document),
      document.fields.locale.value,
    ),
  )
}

const sendEstimateEmailWhenReady = async (estimate: EstimateInterface, email, type, captchaToken, dispatch) => {
  email.entityId = estimate.id
  await dispatch(sendEstimateEmail(email, estimate.id, captchaToken, estimate?.estimatedocument?.fields?.locale?.value))
}

const sendReceiptEmailWhenReady = (receipt, email, captchaToken) => {
  return async (dispatch, getState) => {
    email.entityId = receipt.id
    if (email.saveAsDefaultMessage) {
      const formattedMessageBody = email?.message?.trim().replace(/\n/g, '<br />')
      await EmailApi.postEmailTemplate(DocumentTypes.RECEIPT, receipt.locale || getState().token.locale, {
        body: formattedMessageBody,
      })
    }
    const emailPayload = {
      to: email.to,
      subject: email.subject,
      body: email.message,
      acceptStripePayment: false,
      ...(email.sendCopyToMe ? { bcc: email.sender } : {}),
      attachmentUuids: email.attachments,
      captchaToken,
    }
    if (email?.bcc && email.sendCopyToMe) {
      emailPayload.bcc = [email?.bcc, email.sender].join(',')
    } else if (email?.bcc) {
      emailPayload.bcc = email?.bcc
    } else if (email.sendCopyToMe) {
      emailPayload.bcc = email?.sender
    }
    if (email?.cc) {
      emailPayload.cc = email?.cc
    }

    await dispatch(sendEmail(emailPayload, DocumentTypes.RECEIPT, receipt.id))
  }
}

const sendDeliveryNoteEmailWhenReady = (deliveryNote, email, captchaToken) => {
  return async (dispatch, getState) => {
    email.entityId = deliveryNote.id
    if (email.saveAsDefaultMessage) {
      const formattedMessageBody = email?.message?.trim().replace(/\n/g, '<br />')
      await EmailApi.postEmailTemplate(DocumentTypes.DELIVERYNOTE, deliveryNote.locale || getState().token.locale, {
        body: formattedMessageBody,
      })
    }
    const emailPayload = {
      to: email.to,
      subject: email.subject,
      body: email.message,
      acceptStripePayment: false,
      ...(email.sendCopyToMe ? { bcc: email.sender } : {}),
      attachmentUuids: email.attachments,
      captchaToken,
    }
    if (email?.bcc && email.sendCopyToMe) {
      emailPayload.bcc = [email?.bcc, email.sender].join(',')
    } else if (email?.bcc) {
      emailPayload.bcc = email?.bcc
    } else if (email.sendCopyToMe) {
      emailPayload.bcc = email?.sender
    }
    if (email?.cc) {
      emailPayload.cc = email?.cc
    }

    await dispatch(sendEmail(emailPayload, DocumentTypes.DELIVERYNOTE, deliveryNote.id))
  }
}

const closeEmailModalAndNavigateToList = (dispatch, state, type: DocumentTypes, id: number) => {
  const path = id ? `/deliverynotes/approved/${id}` : '/deliverynotes/approved'

  switch (type) {
    case DocumentTypes.RECEIPT:
      dispatch(setNavigationPath(`/receipts/approved/${id}`))
      break
    case DocumentTypes.DELIVERYNOTE:
      dispatch(setNavigationPath(path))
      break
    default:
      dispatch(setNavigation(getNavigationToList(type, id, state)))
  }
  dispatch(navigateRemoveModal('EmailModal'))
}

export function approveInvoiceDraftAndSendEmail(email: InvoiceEmailInterface, sendOption, captchaToken) {
  return async (dispatch, getState) => {
    dispatch(showGeneratingDocumentSpinner())
    try {
      // save draft
      const { draft: stateDraft, settings } = getState()
      const draft = handleFieldsBeforeSave(stateDraft, settings)
      if (email.attachXRechnung && email.buyerReference) {
        draft.fields.buyerReference = { name: 'buyerReference', value: email.buyerReference, visible: true }
        delete email.buyerReference
      }
      draft.toBeApproved = true
      await dispatch(exportedActions.saveInvoiceDraft(draft, false))
      const { draft: savedDraft, company } = getState()

      // approve draft
      const invoices: InvoiceInterface[] = await dispatch(
        approveInvoiceDraft(savedDraft, company?.isAgerasNextMigration),
      )

      // send email
      if (sendOption === 'all') {
        await Promise.all(
          invoices.map((i: InvoiceInterface) => {
            const singleEmail = Immutable.copy(email)
            const order = i.invoicedocument?.order
            if (order && ['downPaymentAdvanceInvoice', 'schedulesDifferentInvoices'].includes(order.orderType)) {
              const filename = constructDocumentFileName(i.invoicedocument)
              singleEmail.subject = String(filename)
            }
            void sendInvoiceEmailWhenReady(i, singleEmail, DocumentTypes.INVOICE, captchaToken, dispatch)
          }),
        )
      } else {
        const invoice = invoices[0]
        await sendInvoiceEmailWhenReady(invoice, email, DocumentTypes.INVOICE, captchaToken, dispatch)
      }
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.INVOICE, invoices[0].id)
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export function approveEstimateDraftAndSendEmail(email: InvoiceEmailInterface, captchaToken) {
  return async (dispatch, getState) => {
    dispatch(showGeneratingDocumentSpinner())
    try {
      // save draft
      const { draft: stateDraft, settings } = getState()
      const draft = handleFieldsBeforeSave(stateDraft, settings)
      await dispatch(saveDraft(draft, false))
      const savedDraft = getState().draft

      // approve draft
      await dispatch(approveDraft(savedDraft))
      const estimate = getState().estimate
      await sendEstimateEmailWhenReady(estimate, email, DocumentTypes.ESTIMATE, captchaToken, dispatch)
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.ESTIMATE, estimate.id)
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export const approveReceiptDraftAndSendEmail =
  (email: InvoiceEmailInterface, captchaToken): IThunkAction =>
  async (dispatch, getState) => {
    dispatch(showGeneratingDocumentSpinner())
    let receipt
    try {
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(approveReceiptDraft())
      receipt = getDraftV1Selector(getState())
      await dispatch(sendReceiptEmailWhenReady(receipt, email, captchaToken))
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.RECEIPT, receipt.id)
      dispatch(clearDraftV1())
    } catch (error) {
      const id = receipt ? receipt?.id : null
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.RECEIPT, id)
      dispatch(navigateRemoveModal('EmailModal'))
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }

export const approveDeliverynoteDraftAndSendEmail =
  (email: InvoiceEmailInterface, captchaToken): IThunkAction =>
  async (dispatch, getState) => {
    let deliveryNote
    try {
      dispatch(showGeneratingDocumentSpinner())
      deliveryNote = await dispatch(approveDeliverynoteDraft())
      await dispatch(sendDeliveryNoteEmailWhenReady(deliveryNote, email, captchaToken))
      if (deliveryNote) {
        closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.DELIVERYNOTE, deliveryNote?.id)
      }
    } catch (error) {
      const id = deliveryNote ? deliveryNote?.id : null
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.DELIVERYNOTE, id)
      dispatch(navigateRemoveModal('EmailModal'))
    } finally {
      dispatch(clearDraftV1())
      dispatch(hideGeneratingDocumentSpinner())
    }
  }

export function updateInvoiceAndSendEmail(email: InvoiceEmailInterface, captchaToken, invoice?: InvoiceInterface) {
  return async (dispatch, getState) => {
    try {
      const draft: DraftInterface = invoice ? invoice.invoicedocument : getState().draft
      dispatch(showGeneratingDocumentSpinner())
      if (email.attachXRechnung && email.buyerReference) {
        draft.fields.buyerReference = { name: 'buyerReference', value: email.buyerReference, visible: true }
        delete email.buyerReference
      }
      if (!draft.invoiceId && invoice) {
        draft.invoiceId = invoice.id
      }
      await dispatch(updateInvoice(draft))
      invoice = invoice || getState().invoice
      await sendInvoiceEmailWhenReady(invoice, email, DocumentTypes.INVOICE, captchaToken, dispatch)
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.INVOICE, invoice.id)
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export function updateEstimateAndSendEmail(email: InvoiceEmailInterface, captchaToken) {
  return async (dispatch, getState) => {
    try {
      const { draft } = getState()
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(updateEstimate(draft))
      const estimate = getState().estimate
      await sendEstimateEmailWhenReady(estimate, email, DocumentTypes.ESTIMATE, captchaToken, dispatch)
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.ESTIMATE, estimate.id)
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export const updateReceiptAndSendEmail =
  (email: InvoiceEmailInterface, captchaToken): IThunkAction =>
  async (dispatch, getState) => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(updateApprovedThunkV1(DocumentTypes.RECEIPT))
      const receipt = getDraftV1Selector(getState())
      await dispatch(sendReceiptEmailWhenReady(receipt, email, captchaToken))
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.RECEIPT, receipt.id)
    } finally {
      dispatch(clearDraftV1())
      dispatch(hideGeneratingDocumentSpinner())
    }
  }

export const updateDeliveryNoteAndSendEmail =
  (email: InvoiceEmailInterface, captchaToken): IThunkAction =>
  async (dispatch, getState) => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(updateApprovedThunkV1(DocumentTypes.DELIVERYNOTE))
      const deliveryNote = getDraftV1Selector(getState())
      await dispatch(sendDeliveryNoteEmailWhenReady(deliveryNote, email, captchaToken))
      closeEmailModalAndNavigateToList(dispatch, getState(), DocumentTypes.DELIVERYNOTE, deliveryNote.id)
    } finally {
      dispatch(clearDraftV1())
      dispatch(hideGeneratingDocumentSpinner())
    }
  }

function removeLineBreaks(message) {
  return message.replace(/<br>|<br\/>/g, '\n')
}

const getDefaultEmailBody = ({ documentType, locale, emailTemplate }) => {
  if (documentType === DocumentTypes.ESTIMATE && emailTemplate.isDefault && locale === 'en_GB') {
    return 'Hi,<br><br>Please find your estimate attached to this email.<br><br>Thanks for requesting an estimate from us!<br><br>Best regards,<br>'
  }

  return emailTemplate.body || l('email.invoicesend.body')
}

function constructDocumentEmailBody(tradeName, defaultBody) {
  return `${removeLineBreaks(defaultBody)}${tradeName}`
}

const getSimpleEmailProps = async (draft, dispatch, state: StateInterface) => {
  const documentType =
    typeof draft.documentType !== 'undefined' ? draft.documentType.replace('Draft', '') : draft.document

  const templateLocale = draft.fields.locale.value
  const emailTemplate = await EmailApi.getEmailTemplate(documentType, templateLocale)
  const sender = getEmailAddress(state)
  const receiver = draft.receiver.email
  const filename = constructDocumentFileName(draft)
  const subject = Array.isArray(filename) ? null : filename
  const templateDocType = draft.document === DocumentTypes.CREDITNOTE ? DocumentTypes.INVOICE : draft.document
  const tradeName = getTradeName(state.settings, templateDocType).name
  const defaultEmailBody = getDefaultEmailBody({ documentType: templateDocType, locale: templateLocale, emailTemplate })
  const message = emailTemplate.isDefault
    ? constructDocumentEmailBody(tradeName, defaultEmailBody)
    : removeLineBreaks(emailTemplate.body)
  const supportsPayNow = isPayNowConnected(state)
  const supportsTinkPayment = hasModuleAvailable(state.settings, moduleNames.tinkPayment)
  const subscription = state.subscriptionManagement.companySubscription.subscription
  const acceptStripePayment = isPayNowActive(state)
  const hasUnpaidConsumablesInvoices = hasDueOrDisputedConsumables(subscription)
  const tinkPaymentEnabled = Boolean(state.subscriptionManagement?.settings?.settings?.tinkPaymentEnabled)
  const noFreeTinkConsumables = hasUsedFreeTinkUnpaidPlan(state)
  const hasIBANPaymentMethod = Boolean(getDefaultIBAN(draft.paymentMeans))
  const props = {
    close: () => dispatch(navigateRemoveModal('EmailModal')),
    showAttachmentIntermediaryModal: () => dispatch(showAttachmentIntermediaryModal()),
    showConsumablesIntermediaryModal: () => dispatch(showConsumablesIntermediaryModal()),
    documentType: draft.document,
    hasAttachmentAccess: hasAttachmentAccess(subscription),
    showSaveAsCheckbox: true,
    supportsPayNow,
    supportsTinkPayment,
    hasIBANPaymentMethod,
    noFreeTinkConsumables,
    hasUnpaidConsumablesInvoices,
    emails: receiver,
    company: draft.invoicer,
    customer: draft.receiver,
    invoiceEmail: {
      sender,
      to: receiver,
      subject,
      message: message || l('email.invoicesend.body'),
      acceptStripePayment,
      tinkPaymentEnabled,
      saveAsDefaultMessage: false,
      sendCopyToMe: false,
      entityId: null,
      attachments: [],
    },
    filename,
    captchaToken: '',
    sendOption: 'all',
  }
  return props
}

const getSimpleEmailPropsV1 = async (draft: EditedDraftV1Interface, dispatch, state: StateInterface) => {
  const documentType = draft.documentType.replace('Draft', '')
  const templateLocale = draft.config?.fields?.locale?.value
  const emailTemplate = await EmailApi.getEmailTemplate(documentType as DocumentTypes, templateLocale)
  const sender = getEmailAddress(state)
  const receiver = draft.customer.email
  const filename = constructDocumentFileNameV1(draft)
  const subject = filename
  const tradeName = tradeNameV1Selector(state, draft?.company?.tradeName._id).tradeName.name
  const message = emailTemplate.isDefault
    ? constructDocumentEmailBody(tradeName, emailTemplate.body || l('email.invoicesend.body'))
    : removeLineBreaks(emailTemplate.body)
  const supportsPayNow = isPayNowConnected(state)
  const subscription = state.subscriptionManagement.companySubscription.subscription
  const acceptStripePayment = isPayNowActive(state)
  const props = {
    close: () => dispatch(navigateRemoveModal('EmailModal')),
    showAttachmentIntermediaryModal: () => dispatch(showAttachmentIntermediaryModal()),
    customer: draft.customer,
    company: draft.company,
    documentType,
    hasAttachmentAccess: hasAttachmentAccess(subscription),
    showSaveAsCheckbox: true,
    supportsPayNow,
    emails: receiver,
    invoiceEmail: {
      sender,
      to: receiver,
      subject,
      message: message || l('email.invoicesend.body'),
      acceptStripePayment,
      saveAsDefaultMessage: false,
      sendCopyToMe: false,
      entityId: null,
      attachments: [],
    },
    filename,
    captchaToken: '',
    sendOption: 'all',
  }
  return props
}

export function showSimpleEmailModal() {
  return async (dispatch, getState) => {
    const state = getState()
    const draft: DraftInterface = state.draft
    if (draft?.receiver?.email === '') {
      const customer = await useGetCustomer(draft?.receiver?.id)
      draft.receiver.email = customer?.email
    }
    const country = draft.receiver ? draft.receiver.address.country : draft?.customer?.address?.country
    const canAttachXRechnung = isXRechnungSupportedCountry(country) && !draft.order
    const props = {
      ...(await getSimpleEmailProps(draft, dispatch, getState())),
      sendEmail: async (email: InvoiceEmailInterface, captchaToken, sendOption: SendOption) => {
        if (draft.document === DocumentTypes.ESTIMATE) {
          draft.editInvoice
            ? dispatch(updateEstimateAndSendEmail(email, captchaToken))
            : dispatch(approveEstimateDraftAndSendEmail(email, captchaToken))
        } else {
          draft.editInvoice
            ? dispatch(updateInvoiceAndSendEmail(email, captchaToken))
            : dispatch(approveInvoiceDraftAndSendEmail(email, sendOption, captchaToken))
        }
      },
      draft,
      attachmentUploadEndpoint: undefined,
      attachmentDeleteEndpoint: undefined,
      showAttachmentOptions:
        [DocumentTypes.INVOICE, DocumentTypes.CREDITNOTE].includes(draft.document) && canAttachXRechnung,
      documentId: draft.invoiceId,
      buyerReference: draft.fields && draft.fields.buyerReference ? draft.fields.buyerReference.value : null,
    }
    if (draft.invoiceId) {
      props.attachmentUploadEndpoint = attachmentUploadEndpoint(DocumentTypes.INVOICE, draft.invoiceId)
      props.attachmentDeleteEndpoint = attachmentDeleteEndpoint(DocumentTypes.INVOICE, draft.invoiceId)
    }
    dispatch(
      navigateAddModal({
        component: 'EmailModal',
        props,
      }),
    )
  }
}

export function showSimpleEmailModalForInvoice(invoice: InvoiceInterface) {
  return async (dispatch, getState) => {
    const draft: DraftInterface = invoice.invoicedocument || invoice.creditnotedocument
    if (draft?.receiver?.email === '') {
      const customer = await useGetCustomer(draft?.receiver?.id)
      draft.receiver.email = customer?.email
    }
    const canAttachXRechnung = isXRechnungSupportedCountry(draft.receiver.address.country) && !draft.order
    const props = {
      ...(await getSimpleEmailProps(draft, dispatch, getState())),
      sendEmail: (email: InvoiceEmailInterface, captchaToken) => {
        if (email.attachXRechnung) {
          dispatch(updateInvoiceAndSendEmail(email, captchaToken, invoice))
        } else {
          sendInvoiceEmailWhenReady(invoice, email, DocumentTypes.INVOICE, captchaToken, dispatch)
        }
        dispatch(navigateRemoveModal('EmailModal'))
      },
      draft,
      attachmentUploadEndpoint: attachmentUploadEndpoint(DocumentTypes.INVOICE, invoice.id),
      attachmentDeleteEndpoint: attachmentDeleteEndpoint(DocumentTypes.INVOICE, invoice.id),
      showAttachmentOptions:
        [DocumentTypes.INVOICE, DocumentTypes.CREDITNOTE].includes(draft.document) && canAttachXRechnung,
      documentId: invoice.id,
      buyerReference: draft.fields.buyerReference ? draft.fields.buyerReference.value : null,
    }
    dispatch(
      navigateAddModal({
        component: 'EmailModal',
        props,
      }),
    )
  }
}

export function showSimpleEmailModalForEstimate(estimate: EstimateInterface) {
  return async (dispatch, getState) => {
    const draft = estimate.estimatedocument
    if (draft?.receiver?.email === '') {
      const customer = await useGetCustomer(draft?.receiver?.id)
      draft.receiver.email = customer?.email
    }
    const props = {
      ...(await getSimpleEmailProps(draft, dispatch, getState())),
      sendEmail: (email: InvoiceEmailInterface, captchaToken) => {
        sendEstimateEmailWhenReady(estimate, email, DocumentTypes.ESTIMATE, captchaToken, dispatch)
        dispatch(navigateRemoveModal('EmailModal'))
      },
      attachmentUploadEndpoint: attachmentUploadEndpoint(DocumentTypes.ESTIMATE, estimate.id),
      attachmentDeleteEndpoint: attachmentDeleteEndpoint(DocumentTypes.ESTIMATE, estimate.id),
    }
    dispatch(
      navigateAddModal({
        component: 'EmailModal',
        props,
      }),
    )
  }
}

export function showEstimateEmailModal(estimateId: number) {
  return async (dispatch, getState) => {
    const estimate = (await getDocument(DocumentTypes.ESTIMATE, estimateId)) as EstimateInterface
    dispatch(showSimpleEmailModalForEstimate(estimate))
  }
}

export const showSimpleEmailModalV1 = (
  document: EditedDraftV1Interface,
  sendEmail?: (email: InvoiceEmailInterface, captchaToken) => Promise<void>,
): IThunkAction<null, ModalInterface, Promise<void>> => {
  return async (dispatch, getState) => {
    const props = {
      ...(await getSimpleEmailPropsV1(document, dispatch, getState())),
      sendEmail:
        sendEmail ||
        ((email: InvoiceEmailInterface, captchaToken, sendOption: SendOption) => {
          if (document.documentType === 'deliverynote') {
            dispatch(updateDeliveryNoteAndSendEmail(email, captchaToken))
          } else if (document.documentType === 'deliverynoteDraft') {
            dispatch(approveDeliverynoteDraftAndSendEmail(email, captchaToken))
          } else if (document.documentType === 'receipt') {
            dispatch(updateReceiptAndSendEmail(email, captchaToken))
          } else if (document.documentType === 'receiptDraft') {
            dispatch(approveReceiptDraftAndSendEmail(email, captchaToken))
          }
        }),
      attachmentUploadEndpoint: undefined,
      attachmentDeleteEndpoint: undefined,
      showAttachmentOptions: false,
      buyerReference: document.config.fields.buyerReference ? document.config.fields.buyerReference.value : null,
    }
    dispatch(
      navigateAddModal({
        component: 'EmailModal',
        props,
      }),
    )
  }
}

export function showReceiptEmailModal(receiptId: number): IThunkAction {
  return async (dispatch) => {
    const receipt = await getDocumentV1(DocumentTypes.RECEIPT, receiptId)
    await dispatch(
      showSimpleEmailModalV1(receipt, async (email: InvoiceEmailInterface, captchaToken) => {
        await dispatch(sendReceiptEmailWhenReady(receipt, email, captchaToken))
        dispatch(navigateRemoveModal('EmailModal'))
      }),
    )
  }
}

export function showDeliveryNoteEmailModal(deliveryNoteId: number): IThunkAction {
  return async (dispatch) => {
    const deliveryNote = await getDocumentV1(DocumentTypes.DELIVERYNOTE, deliveryNoteId)
    await dispatch(
      showSimpleEmailModalV1(deliveryNote, async (email: InvoiceEmailInterface, captchaToken) => {
        await dispatch(sendDeliveryNoteEmailWhenReady(deliveryNote, email, captchaToken))
        dispatch(navigateRemoveModal('EmailModal'))
      }),
    )
  }
}

export const getNavigationToList = (
  documentType: DocumentType,
  documentId: number,
  state: StateInterface,
): NavigationInterface => {
  const { settings, navigation } = state
  const module = settings.modules.find((mod) => mod.name === 'reactInvoiceList' && !mod.hidden)
  const docType = documentType === DocumentTypes.CREDITNOTE ? 'creditNote' : documentType
  // eslint-disable-next-line no-nested-ternary
  const mod = module ? (docType === DocumentTypes.ESTIMATE ? 'reactEstimateList' : 'reactInvoiceList') : 'invoices'
  if (documentId) {
    const reactPath = `/${mod}/${docType}/${documentId}`
    const path = module ? reactPath : `${mod}`
    return {
      path,
      app: {
        ...navigation.app,
        mod,
        'tabs-invoices': `${documentType}s`,
        [documentType]: documentId,
      },
    }
  }
}

function _createEstimateEmail(
  email: InvoiceEmailInterface,
  state: StateInterface,
  captchaToken: string,
): InvoiceDeliveryEmailInterface {
  const emailPayload = {
    to: email.to,
    subject: email.subject,
    body: email.message,
    acceptStripePayment: false,
    ...(email.sendCopyToMe ? { bcc: email.sender } : {}),
    attachmentUuids: email.attachments,
    captchaToken,
  }
  if (email?.bcc && email.sendCopyToMe) {
    emailPayload.bcc = [email?.bcc, email.sender].join(',')
  } else if (email?.bcc) {
    emailPayload.bcc = email?.bcc
  } else if (email.sendCopyToMe) {
    emailPayload.bcc = email?.sender
  }
  if (email?.cc) {
    emailPayload.cc = email?.cc
  }
  return emailPayload
}

export const checkAttachmentIntermediaryModal = (dispatch) => {
  const openSubscriptionModal = () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          origin: PremiumModalOrigin.EmailAttachment,
          hideDowngradePlans: true,
        },
      }),
    )
  const openIntermediary = () => {
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.EMAIL_ATTACHMENT,
          next: openSubscriptionModal,
        },
      }),
    )
  }
  openIntermediary()
}

export const showAttachmentIntermediaryModal = () => {
  return async (dispatch) => {
    checkAttachmentIntermediaryModal(dispatch)
  }
}

export const checkConsumablesIntermediaryModal = (dispatch) => {
  const openSubscriptionModal = () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          origin: PremiumModalOrigin.EmailConsumablesCheckBox,
          hideDowngradePlans: true,
          next: () => {
            dispatch(navigateRemoveModal('EmailModal'))
            dispatch(getSubscription())
          },
        },
      }),
    )
  const openIntermediary = () => {
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.CONSUMABLES,
          next: openSubscriptionModal,
        },
      }),
    )
  }
  openIntermediary()
}

export const showConsumablesIntermediaryModal = () => {
  return async (dispatch) => {
    checkConsumablesIntermediaryModal(dispatch)
  }
}
